import { forwardRef, useImperativeHandle, useState } from "react";
import { Box } from "@mui/material";
import { useSnackbar } from "notistack";
import { FilePond, registerPlugin } from "react-filepond";
import FilePondPluginImageEditor from "filepond-plugin-image-editor";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginFilePoster from "filepond-plugin-file-poster";
import "filepond-plugin-file-poster/dist/filepond-plugin-file-poster.css";

import "filepond/dist/filepond.min.css";
import "./filepond.scss";

import { ProcessServerConfigFunction } from "filepond";
import {
  legacyDataToImageState,
  openEditor,
  createDefaultImageReader,
  createDefaultImageWriter,
  processImage,
  getEditorDefaults,
} from "@pqina/pintura";
import "@pqina/pintura/pintura.css";

import { useDialogs } from "../contexts/useDialogs";
import {
  ImageSelectOptions,
  ROOT_FOLDER,
  combinePath,
} from "../features/images/image";
import { NEED_UPGRADE_ERROR } from "../services/errors";
import { logError } from "../services/logging";
import { uploadFile } from "../features/data/file-util";
import { isHeicFile, refuseHeic } from "../features/images/heic";

registerPlugin(
  //FilePondPluginImageExifOrientation,
  //FilePondPluginImagePreview,
  FilePondPluginFileValidateSize,
  FilePondPluginFileValidateType,
  //FilePondPluginImageCrop,
  //FilePondPluginImageResize,
  //FilePondPluginImageTransform,
  FilePondPluginImageEditor,
  FilePondPluginFilePoster
);

// One pending file, as the parent needs to see it: FilePond's own id (stable, and
// the only way to tell two files with the SAME name apart) plus the name we would
// upload it under.
export type PendingFile = { id: string; name: string };

interface FileUploadParams {
  options: ImageSelectOptions;
  // `files` are the pending files, so the parent can warn about a name that already
  // exists in the library. The server overwrites a same-named blob without asking,
  // and every product pointing at that path changes with it.
  onChange: (count: number, files: PendingFile[]) => void;
  onUploadFinish: (success: boolean) => void;
  onFileUpload: (path: string, fileSize: number, error) => void;
  minHeight?: string;
}

export type FileUploadRef = {
  upload: () => void;
  reset: () => void;
  // Upload these files under a different name, keyed by FilePond id. Stored as file
  // metadata rather than by rewriting the File, so the image editor's own copy is
  // untouched. Silent, so it does not re-fire onupdatefiles mid-edit.
  applyNames: (names: Record<string, string>) => void;
};

export const FileUpload = forwardRef<FileUploadRef, FileUploadParams>(
  (
    {
      onChange,
      onUploadFinish,
      onFileUpload,
      options,
      minHeight,
    }: FileUploadParams,
    ref: React.MutableRefObject<FileUploadRef>
  ) => {
    const [pond, setPond] = useState<FilePond | null>();

    const { openUpgradeModal } = useDialogs();
    const { enqueueSnackbar } = useSnackbar();

    // Adding the very same file twice is always a slip, and it used to upload twice:
    // both copies target one blob path, FilePond uploads in parallel, and the loser
    // comes back 412 ConditionNotMet from Azure. Name alone is not enough to judge —
    // two different pictures can share a name, and that case is legitimate (the
    // duplicate-name warning handles it) — so match on size and mtime too.
    const isSameFileAlreadyAdded = (item) => {
      const file = item?.file;
      if (!file) return false;
      return !!pond
        ?.getFiles()
        ?.some(
          (f) =>
            f.id !== item.id &&
            f.file?.name === file.name &&
            f.file?.size === file.size &&
            f.file?.lastModified === file.lastModified
        );
    };
    useImperativeHandle(ref, () => ({
      upload: () => {
        pond?.processFiles();
      },
      reset: () => {
        pond?.removeFiles();
      },
      applyNames: (names) => {
        pond?.getFiles().forEach((f) => {
          const name = names[f.id];
          if (name) f.setMetadata("targetName", name, true);
        });
      },
    }));

    const processHandler: ProcessServerConfigFunction = async (
      _fieldName,
      file,
      metadata,
      load,
      error,
      progress,
      abort
    ) => {
      const uploadFolder =
        options.folder === ROOT_FOLDER ? "/" : options.folder;
      // `targetName` is set by the parent's "Rename to keep both" fix; without it we
      // upload under the file's own name, which is what overwrites a same-named blob.
      const fileName = options.path
        ? options.path
        : combinePath(uploadFolder, metadata?.targetName ?? file.name);
      const onload = (request) => {
        if (request.status >= 200 && request.status < 300) {
          load(request.responseText);
        } else {
          if (request.responseText === NEED_UPGRADE_ERROR) {
            openUpgradeModal({ type: "images", entryPoint: "image-upload" });
          }
          error("upload error");
          onUploadFinish(false);
        }
      };
      const onprogress = (e) => {
        progress(e.lengthComputable, e.loaded, e.total);
      };
      try {
        await uploadFile(
          "image",
          file,
          fileName,
          null,
          onload,
          onprogress,
          abort
        );
      } catch (e) {
        // uploadFile rejects only on a network-level failure (XHR onerror/ontimeout) or a
        // missing file - an HTTP error status goes through onload above. FilePond is
        // callback-driven and ignores this promise, so without this catch the rejection
        // escaped as an unhandled rejection (CM2-PF: 457 events / 115 users since 2022)
        // and FilePond was never told, leaving the file spinning forever with no message.
        // logError keeps the Sentry/App Insights signal once the UI stops leaking it.
        logError(e, "image upload");
        error(
          e instanceof Error
            ? e.message
            : "Upload failed. Check your connection and try again."
        );
        onUploadFinish(false);
      }
    };

    return (
      <Box
        sx={{
          "& .filepond--wrapper .filepond--drop-label": {
            minHeight: pond?.getFiles()?.length > 0 ? 150 : minHeight || 150,
          },
        }}
        className={options.multi ? "multi-upload" : null}
      >
        <FilePond
          ref={(ref) => setPond(ref)}
          name="file"
          allowMultiple={true}
          allowImageEditor={true}
          maxFiles={options.multi ? 50 : 1}
          instantUpload={false}
          // Windows gives a HEIC no MIME type, so the type check below would reject it first
          // with FilePond's generic "File is of invalid type". Naming the type here lets it
          // through to beforeAddFile, which refuses it with the message that says what to do.
          fileValidateTypeDetectType={(source, type) =>
            Promise.resolve(isHeicFile(source) ? "image/heic" : type)
          }
          beforeAddFile={(item) => {
            const heic = refuseHeic([item.file as File], "image-manager");
            if (heic.message) {
              enqueueSnackbar(heic.message, { variant: "warning" });
              return false;
            }
            if (isSameFileAlreadyAdded(item)) {
              enqueueSnackbar(
                `${item.file?.name} is already in this upload, not added again`,
                { variant: "warning" }
              );
              return false;
            }
            return true;
          }}
          onupdatefiles={(fileItems) => {
            // Read the name off the File itself, not fileItem.filename — the Pintura
            // editor swaps the file in place, and processHandler uploads `file.name`.
            onChange(
              fileItems.length,
              fileItems.map((f) => ({
                id: f.id,
                name: f.file?.name ?? f.filename,
              }))
            );
          }}
          onprocessfiles={() => onUploadFinish(true)}
          onprocessfile={(error, file) => {
            // file is null when an upload is aborted/failed (error is set), so guard the
            // deref — the downstream handler ignores path/size on the error path anyway (CM2-2GJ).
            onFileUpload(file?.serverId ?? "", file?.fileSize ?? 0, error);
          }}
          server={{
            process: processHandler,
          }}
          acceptedFileTypes={["image/*"]}
          //filePosterMaxHeight={256}
          // FilePond Image Editor plugin properties
          imageEditor={{
            // Maps legacy data objects to new imageState objects (optional)
            legacyDataToImageState: legacyDataToImageState,

            // Used to create the editor (required)
            createEditor: openEditor,

            // Used for reading the image data. See JavaScript installation for details on the `imageReader` property (required)
            imageReader: [
              createDefaultImageReader,
              {
                // createDefaultImageReader options here
              },
            ],

            // Can leave out when not generating a preview thumbnail and/or output image (required)
            imageWriter: [
              createDefaultImageWriter,
              {
                // CM2-2K9: Safari's canvas.toBlob returns null past its canvas memory ceiling, which
                // crashed the write on full-resolution photos. The server keeps at most 1600px
                // (ImageManager.GetMaxDimension), so 2048 discards nothing it would keep; raise this
                // with it if the paid max ever goes above 2048.
                targetSize: {
                  width: 2048,
                  height: 2048,
                  fit: "contain",
                  upscale: false,
                },
              },
            ],

            // Used to poster and output images, runs an invisible "headless" editor instance.
            imageProcessor: processImage,

            // Pintura Image Editor options
            editorOptions: {
              // Pass the editor default configuration options
              ...getEditorDefaults(),

              // This will set a square crop aspect ratio
              //imageCropAspectRatio: 1,
            },
          }}
        />
      </Box>
    );
  }
);

FileUpload.displayName = "FileUpload";
